import Link from '@/lib/compat/link';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/lib/i18n/client';
import type { PageHeaderVariant, BackNavigationProps } from './types';

interface PageHeaderDesktopProps extends BackNavigationProps {
  variant: PageHeaderVariant;
  title: string;
  subtitle?: string;
  /** Optional list total. Zero is intentionally hidden because it adds no orientation value. */
  count?: number;
  /** Accessible label for the count badge, for example "orders" or "shipments". */
  countLabel?: string;
  actions?: React.ReactNode;
  headerExtra?: React.ReactNode; // for badges, avatars, etc.
}

export function PageHeaderDesktop({
  variant,
  title,
  subtitle,
  count,
  countLabel,
  backHref,
  backLabel,
  onBackClick,
  actions,
  headerExtra,
}: PageHeaderDesktopProps) {
  const t = useTranslations();
  const showBackButton = variant === 'detail' || variant === 'form';
  const isList = variant === 'list';

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    }
  };

  return (
    <div className={cn(
      'flex justify-between gap-4',
      // A list page's job is its table, so its header stays one compact row.
      isList ? 'mb-4 items-center' : 'mb-5 items-end border-b border-border/80 pb-5',
    )}>
      {/* Left side: Back button + Title/Subtitle */}
      <div className="flex items-center gap-4">
        {showBackButton && (
          <>
            {backHref ? (
              <Button variant="ghost" size="icon" asChild>
                <Link href={backHref} aria-label={backLabel || t('common.back')}>
                  <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackClick}
                aria-label={backLabel || t('common.back')}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </>
        )}

        {/* Title and subtitle */}
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className={cn(
              'truncate font-bold leading-tight text-foreground',
              isList ? 'text-2xl tracking-[-0.02em]' : 'text-[2rem] tracking-[-0.035em]',
            )}>
              {title}
            </h1>
            {typeof count === 'number' && count > 0 && (
              <span
                className="shrink-0 text-sm tabular-nums text-muted-foreground"
                aria-label={countLabel ? `${count} ${countLabel}` : String(count)}
              >
                {count}
              </span>
            )}
            {headerExtra && <div className="shrink-0">{headerExtra}</div>}
          </div>
          {subtitle && !isList && (
            <p className="mt-1.5 text-base leading-6 text-muted-foreground">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Right side: Actions */}
      {actions && (
        <div className="flex items-center gap-2 shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}
